import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import dayjs from 'dayjs';

interface PrintableRegistrationsProps {
  clubs: any[];
  competitionName?: string;
}

const PrintableRegistrations: React.FC<PrintableRegistrationsProps> = ({ clubs, competitionName = 'Wettkampf' }) => {
  
  // 🔹 NEUE Funktion: Gebühren mit Priorisierung der Meldebestätigung
  const getFeeData = (club: any) => {
    // Priorität 1: Meldebestätigung (falls vorhanden)
    if (club.confirmation?.fees?.totalAmount) {
      return {
        totalFeeElite: club.confirmation.fees.totalFeeElite || 0,
        totalFeeBasic: club.confirmation.fees.totalFeeBasic || 0,
        totalPenaltyFeeElite: club.confirmation.fees.totalPenaltyFeeElite || 0,
        totalPenaltyFeeBasic: club.confirmation.fees.totalPenaltyFeeBasic || 0,
        totalTShirtFee: club.confirmation.fees.totalTShirtFee || 0,
        totalCateringFee: club.confirmation.fees.totalCateringFee || 0,
        totalOvernightFee: club.confirmation.fees.totalOvernightFee || 0,
        totalAmount: club.confirmation.fees.totalAmount,
        source: 'Bestätigung'
      };
    }

    // Priorität 2: Berechnete Gebühren aus aktueller Meldung
    return {
      totalFeeElite: club.totalFeeElite || 0,
      totalFeeBasic: club.totalFeeBasic || 0, // 🔹 Korrigierte Feldnamen!
      totalPenaltyFeeElite: club.totalPenaltyFeeElite || 0,
      totalPenaltyFeeBasic: club.totalPenaltyFeeBasic || 0, // 🔹 Korrigierte Feldnamen!
      totalTShirtFee: club.totalTShirtFee || 0,
      totalCateringFee: club.totalCateringFee || 0,
      totalOvernightFee: club.totalOvernightFee || 0,
      totalAmount: (club.totalFee || 0) + (club.totalPenaltyFee || 0) + (club.totalTShirtFee || 0) + (club.totalCateringFee || 0) + (club.totalOvernightFee || 0),
      source: 'Berechnet'
    };
  };

  // 🔹 NEUE Funktion: Zahlungsstatus mit korrekter Logik
  const getPaymentStatus = (club: any) => {
    if (!club.confirmation?.payment) return 'Nein';
    
    const fees = getFeeData(club);
    const paidAmount = Number((club.confirmation.payment.amount || 0).toFixed(2));
    const expectedAmount = Number(fees.totalAmount.toFixed(2));
    
    if (paidAmount === expectedAmount) {
      return 'Ja';
    } else if (paidAmount > 0) {
      return `Teilweise (${paidAmount.toFixed(2)}€)`;
    } else {
      return 'Nein';
    }
  };

  // Handler für den Druckvorgang
  const handlePrint = () => {
    // Berechne Gesamtsummen mit korrekter Logik
    const totalSums = clubs.reduce((sums, club) => {
      const fees = getFeeData(club);
      return {
        totalFeeElite: sums.totalFeeElite + fees.totalFeeElite,
        totalFeeBasic: sums.totalFeeBasic + fees.totalFeeBasic,
        totalPenaltyFeeElite: sums.totalPenaltyFeeElite + fees.totalPenaltyFeeElite,
        totalPenaltyFeeBasic: sums.totalPenaltyFeeBasic + fees.totalPenaltyFeeBasic,
        totalRegistrationFees: sums.totalRegistrationFees + fees.totalFeeElite + fees.totalFeeBasic,
        totalTShirtFee: sums.totalTShirtFee + fees.totalTShirtFee,
        totalCateringFee: sums.totalCateringFee + fees.totalCateringFee,
        totalOvernightFee: sums.totalOvernightFee + fees.totalOvernightFee,
        grandTotal: sums.grandTotal + fees.totalAmount
      };
    }, {
      totalFeeElite: 0,
      totalFeeBasic: 0,
      totalPenaltyFeeElite: 0,
      totalPenaltyFeeBasic: 0,
      totalRegistrationFees: 0,
      totalTShirtFee: 0,
      totalCateringFee: 0,
      totalOvernightFee: 0,
      grandTotal: 0
    });

    // Erstelle HTML-Inhalt
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Zahlungsübersicht ${competitionName}</title>
        <meta charset="UTF-8">
        <style>
          body {
            font-family: Arial, sans-serif;
            font-size: 11px;
            margin: 0;
            padding: 10px;
          }
          h1 {
            font-size: 14px;
            margin: 0 0 5px 0;
          }
          p {
            margin: 0 0 10px 0;
            font-size: 11px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 10px;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 4px;
            text-align: left;
          }
          th {
            background-color: #f2f2f2;
            font-weight: bold;
          }
          .right {
            text-align: right;
          }
          .center {
            text-align: center;
          }
          .total-row {
            background-color: #f2f2f2;
            font-weight: bold;
          }
          .source-indicator {
            font-size: 8px;
            color: #666;
          }
          @page {
            size: landscape;
            margin: 1cm;
          }
          @media print {
            body { 
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        <h1>Zahlungsübersicht: ${competitionName}</h1>
        <p>Erstellt am: ${dayjs().format('DD.MM.YYYY HH:mm')}</p>
        <p><span class="source-indicator">* Gebühren aus Meldebestätigung haben Vorrang vor berechneten Werten</span></p>
        
        <table>
          <thead>
            <tr>
              <th>Verein</th>
              <th class="right">Meldegeb. Elite (€)</th>
              <th class="right">Strafgeb. Elite (€)</th>
              <th class="right">Meldegeb. Basis (€)</th>
              <th class="right">Strafgeb. Basis (€)</th>
              <th class="right">Gesamt Meldegeb. (€)</th>
              <th class="right">T-Shirts (€)</th>
              <th class="right">Essen (€)</th>
              <th class="right">Übernachtung (€)</th>
              <th class="right">Gesamt (€)</th>
              <th class="center">Bezahlt</th>
              <th class="center">Quelle</th>
            </tr>
          </thead>
          <tbody>
            ${clubs.map(club => {
              const fees = getFeeData(club);
              const paymentStatus = getPaymentStatus(club);
              
              return `
              <tr>
                <td>${club.clubName}</td>
                <td class="right">${fees.totalFeeElite.toFixed(2)}</td>
                <td class="right">${fees.totalPenaltyFeeElite.toFixed(2)}</td>
                <td class="right">${fees.totalFeeBasic.toFixed(2)}</td>
                <td class="right">${fees.totalPenaltyFeeBasic.toFixed(2)}</td>
                <td class="right">${(fees.totalFeeElite + fees.totalFeeBasic).toFixed(2)}</td>
                <td class="right">${fees.totalTShirtFee.toFixed(2)}</td>
                <td class="right">${fees.totalCateringFee.toFixed(2)}</td>
                <td class="right">${fees.totalOvernightFee.toFixed(2)}</td>
                <td class="right"><b>${fees.totalAmount.toFixed(2)}</b></td>
                <td class="center">${paymentStatus}</td>
                <td class="center source-indicator">${fees.source}</td>
              </tr>
            `;}).join('')}
            
            <tr class="total-row">
              <td>Summe</td>
              <td class="right">${totalSums.totalFeeElite.toFixed(2)}</td>
              <td class="right">${totalSums.totalPenaltyFeeElite.toFixed(2)}</td>
              <td class="right">${totalSums.totalFeeBasic.toFixed(2)}</td>
              <td class="right">${totalSums.totalPenaltyFeeBasic.toFixed(2)}</td>
              <td class="right">${totalSums.totalRegistrationFees.toFixed(2)}</td>
              <td class="right">${totalSums.totalTShirtFee.toFixed(2)}</td>
              <td class="right">${totalSums.totalCateringFee.toFixed(2)}</td>
              <td class="right">${totalSums.totalOvernightFee.toFixed(2)}</td>
              <td class="right"><b>${totalSums.grandTotal.toFixed(2)}</b></td>
              <td></td>
              <td></td>
            </tr>
          </tbody>
        </table>
        
        <div style="margin-top: 20px; font-size: 10px;">
          <h3 style="font-size: 12px; margin: 0 0 10px 0;">Legende:</h3>
          <p><strong>Bestätigung:</strong> Gebühren aus finaler Meldebestätigung (manuell angepasst)</p>
          <p><strong>Berechnet:</strong> Automatisch berechnete Gebühren aus aktueller Meldung</p>
          <p><strong>Bezahlt:</strong></p>
          <ul style="margin: 5px 0; padding-left: 20px;">
            <li><strong>Ja:</strong> Vollständig bezahlt</li>
            <li><strong>Teilweise (Betrag):</strong> Nur teilweise bezahlt</li>
            <li><strong>Nein:</strong> Noch nicht bezahlt</li>
          </ul>
        </div>
        
        <script>
          // Direkt drucken und Fenster schließen
          window.onload = function() {
            window.print();
            setTimeout(function() {
              window.close();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;

    // Öffne ein neues Fenster und schreibe den Inhalt
    const printWindow = window.open('', '_blank', 'width=1000,height=700');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
    }
  };

  return (
    <Tooltip title="Zahlungsübersicht drucken">
      <IconButton color="primary" onClick={handlePrint}>
        <PrintIcon />
      </IconButton>
    </Tooltip>
  );
};

export default PrintableRegistrations;